// @ts-nocheck
/**
 * SubCustomerSelector
 * Inline selector panel for assigning/creating sub-customers on an order.
 * Single unified panel: search at top, results below, inline create form at bottom.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from '@spcc/core'
import { Button, Input, cn, Spinner } from '@spcc/uikit'
import { Search, Plus, X, User, Phone, Check, ChevronDown, ChevronUp } from '@spcc/uikit'
import type { SubCustomer } from '../../domain/types'
import { SubCustomerTypeBadge } from './sub-customer-type'
import { subCustomersApi } from '../../infrastructure/api/sub-customers-api'

interface SubCustomerSelectorProps {
  /** Currently assigned sub-customer (if any) */
  currentSubCustomer?: SubCustomer | null
  /** Called when user selects or creates a sub-customer */
  onSelect: (subCustomer: SubCustomer) => void
  /** Called when user clears the sub-customer */
  onClear?: () => void
  /** Called to close this panel */
  onCancel: () => void
  /** Whether a save is in progress */
  loading?: boolean
  /** Tenant documentId to scope the list to this tenant only (prevents cross-tenant data leakage) */
  tenantDocumentId?: string | null
}

function SubCustomerRow({
  customer,
  isSelected,
  onSelect,
}: {
  customer: SubCustomer
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full flex items-start gap-3 p-3 rounded-lg text-start transition-colors',
        'hover:bg-primary/5 border border-transparent',
        isSelected && 'border-primary/30 bg-primary/5',
      )}
    >
      <div className="mt-0.5 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <User className="w-4 h-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground truncate">{customer.name}</p>
          <SubCustomerTypeBadge type={customer.customerType} />
        </div>
        <p className="text-xs text-muted-foreground font-mono">{customer.code}</p>
        {customer.phone && (
          <p className="text-xs text-muted-foreground mt-0.5">{customer.phone}</p>
        )}
      </div>
      {isSelected && <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />}
    </button>
  )
}

export function SubCustomerSelector({
  currentSubCustomer,
  onSelect,
  onClear,
  onCancel,
  loading = false,
  tenantDocumentId,
}: SubCustomerSelectorProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [customers, setCustomers] = useState<SubCustomer[]>([])
  const [searching, setSearching] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const requestIdRef = useRef(0)

  const loadCustomers = useCallback(async (q: string) => {
    // Only the latest request may update the list (slow older responses are dropped)
    const requestId = ++requestIdRef.current
    setSearching(true)
    try {
      const result = await subCustomersApi.getSubCustomers({ search: q, pageSize: 30, tenantDocumentId })
      if (requestId === requestIdRef.current) setCustomers(result.data)
    } catch {
      if (requestId === requestIdRef.current) setCustomers([])
    } finally {
      if (requestId === requestIdRef.current) setSearching(false)
    }
  }, [tenantDocumentId])

  useEffect(() => {
    clearTimeout(searchTimerRef.current)
    // Load immediately on open / cleared search; debounce only while typing
    searchTimerRef.current = setTimeout(() => {
      loadCustomers(search)
    }, search ? 250 : 0)
    return () => clearTimeout(searchTimerRef.current)
  }, [search, loadCustomers])

  async function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    setCreateError(null)
    try {
      const created = await subCustomersApi.createSubCustomer({
        name: newName.trim(),
        phone: newPhone.trim() || undefined,
        ...(tenantDocumentId ? { tenant: tenantDocumentId } : {}),
      })
      onSelect(created)
    } catch (err: any) {
      setCreateError(err?.message ?? t('subCustomers.saveError'))
    } finally {
      setCreating(false)
    }
  }

  function toggleCreate() {
    setShowCreate((v) => !v)
    setCreateError(null)
    if (!showCreate) {
      setNewName('')
      setNewPhone('')
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder={t('subCustomers.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ps-9"
          autoFocus
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results */}
      <div
        className={cn(
          'flex flex-col gap-1 max-h-48 overflow-y-auto transition-opacity',
          searching && customers.length > 0 && 'opacity-60',
        )}
      >
        {searching && customers.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <Spinner className="w-5 h-5" />
          </div>
        ) : customers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-3">
            {t('subCustomers.noCustomersFound')}
          </p>
        ) : (
          customers.map((c) => (
            <SubCustomerRow
              key={c.id}
              customer={c}
              isSelected={c.id === currentSubCustomer?.id}
              onSelect={() => onSelect(c)}
            />
          ))
        )}
      </div>

      {currentSubCustomer && onClear && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="text-destructive hover:text-destructive text-xs -mt-1"
        >
          <X className="w-3.5 h-3.5 me-1" />
          {t('common.clear')}
        </Button>
      )}

      {/* Inline create toggle */}
      <button
        type="button"
        onClick={toggleCreate}
        className="flex items-center gap-1.5 text-xs text-primary hover:underline font-medium self-start"
      >
        {showCreate ? (
          <>
            <ChevronUp className="w-3.5 h-3.5" />
            {t('subCustomers.hideCreate')}
          </>
        ) : (
          <>
            <Plus className="w-3.5 h-3.5" />
            {t('subCustomers.createNewCustomer')}
          </>
        )}
      </button>

      {/* Inline create form */}
      {showCreate && (
        <div className="flex flex-col gap-3 p-3 rounded-lg border border-border bg-muted/30">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              {t('subCustomers.customerName')} *
            </label>
            <Input
              placeholder={t('subCustomers.namePlaceholder')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              {t('subCustomers.customerPhone')}
            </label>
            <Input
              placeholder={t('subCustomers.phonePlaceholder')}
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              type="tel"
            />
          </div>
          {createError && (
            <p className="text-xs text-destructive">{createError}</p>
          )}
          <Button
            onClick={handleCreate}
            disabled={!newName.trim() || creating}
            size="sm"
            className="w-full"
          >
            {creating ? (
              <Spinner className="w-4 h-4 me-2" />
            ) : (
              <Plus className="w-4 h-4 me-2" />
            )}
            {t('subCustomers.addCustomer')}
          </Button>
        </div>
      )}

      <Button variant="outline" size="sm" onClick={onCancel} disabled={loading} className="w-full">
        {t('common.cancel')}
      </Button>
    </div>
  )
}
